import { STATE } from './camera-handler';
/**
 * @class CameraTorchController
 * @description Manages the activation and deactivation of the camera's torch (flash/light)
 * by applying constraints to the active media track. It relies on an instance
 * of CameraHandler to access the current video track.
 */
export class CameraTorchController {
    _cameraDeviceHandler;
    /**
     * @private
     * @type {boolean}
     * @description Stores the current state of the torch: true if the torch is actively on, false otherwise.
     * Uses the required single underscore prefix for private properties.
     */
    _isTorchCurrentlyOn = false;
    /**
     * @constructor
     * @description Initializes a new instance of the CameraTorchController.
     * @param {CameraHandler} _cameraDeviceHandler - A reference to the CameraHandler instance, used to access the active MediaStreamTrack and check the camera's state.
     */
    constructor(_cameraDeviceHandler) {
        this._cameraDeviceHandler = _cameraDeviceHandler;
    }
    /**
     * @public
     * @type {boolean}
     * @description Gets the current state of the torch.
     * Uses the required double underscore prefix for public accessors (getters).
     * @returns {boolean} True if the torch is currently on, false otherwise.
     * @example
     * const isLightOn = torchController.__getIsCurrentlyOn;
     * console.log(`Torch is on: ${isLightOn}`);
     */
    get __getIsCurrentlyOn() {
        // Returns the value of the private state variable indicating if the torch is on.
        return this._isTorchCurrentlyOn;
    }
    /**
     * @public
     * @type {boolean}
     * @description Checks if the torch feature is available on the currently active media track.
     * Uses the required double underscore prefix for public accessors (getters).
     * @returns {boolean} True if a track is active, the camera is not stopped, and the 'torch' capability is supported.
     * @example
     * const isTorchSupported = torchController.__checkIsSupported;
     * if (isTorchSupported) {
     * await torchController.__toggleTorch(true);
     * }
     */
    get __checkIsSupported() {
        // Retrieves the currently active MediaStreamTrack from the camera handler.
        const track = this._cameraDeviceHandler.__getCurrentTrack;
        // Checks if a track exists AND if the camera handler's state is NOT STOPPED.
        if (!track || this._cameraDeviceHandler.__getCurrentState === STATE.__STOP) {
            // If either condition is true, the torch cannot be controlled, so return false.
            return false;
        }
        // Retrieves the capabilities (supported settings) of the current media track.
        const capabilities = track.getCapabilities();
        // Checks if the 'torch' capability is defined and explicitly a boolean type,
        // which signals support for the torch control.
        return typeof capabilities.torch === 'boolean';
    }
    /**
     * @description Toggles the camera torch state (on/off) or sets it to a specific state.
     * @param {boolean} [turnOnState] - Optional. If true, turns the torch on. If false, turns it off. If undefined, it toggles the current state.
     * @returns {Promise<boolean>} A Promise that resolves to true if the state was successfully changed or is already the desired state, false otherwise (e.g., if torch is unavailable or constraints fail).
     * @public
     * @async
     * @method
     * @example
     * // Toggle the state
     * await torchController.__toggleTorch();
     * // Explicitly turn on
     * await torchController.__toggleTorch(true);
     */
    async __toggleTorch(turnOnState) {
        // Check if the torch feature is available and the stream is active.
        if (!this.__checkIsSupported) {
            return false;
        }
        // Safely retrieve the current track, asserting its existence based on the availability check.
        const track = this._cameraDeviceHandler.__getCurrentTrack;
        // Determine the target state: use the provided `turnOnState` if available, otherwise invert the current state (`this._isTorchCurrentlyOn`).
        // noinspection PointlessBooleanExpressionJS
        const newState = turnOnState !== undefined ? !!turnOnState : !this._isTorchCurrentlyOn;
        // Check if the determined new state is the same as the current state.
        if (newState === this._isTorchCurrentlyOn) {
            return true;
        }
        try {
            await track.applyConstraints({
                advanced: [{ torch: newState }],
            });
            this._isTorchCurrentlyOn = newState;
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * @public
     * @async
     * @method
     * @description Explicitly turns the camera torch ON.
     * @returns {Promise<boolean>} A Promise that resolves to true if the torch was turned on or is already on, false otherwise.
     * @example
     * await torchController.__turnOnTorch();
     */
    async __turnOnTorch() {
        // Calls the __toggleTorch method, explicitly passing 'true' to ensure the torch is turned ON.
        return this.__toggleTorch(true);
    }
    /**
     * @public
     * @async
     * @method
     * @description Explicitly turns the camera torch OFF.
     * @returns {Promise<boolean>} A Promise that resolves to true if the torch was turned off or is already off, false otherwise.
     * @example
     * await torchController.__turnOffTorch();
     */
    async __turnOffTorch() {
        // Calls the __toggleTorch method, explicitly passing 'false' to ensure the torch is turned OFF.
        return this.__toggleTorch(false);
    }
}
//# sourceMappingURL=camera-torch-controller.js.map